const StatPill = ({
  icon,
  label,
  value,
  isLoading,
  isClickable = false,
  onClick,
  accent = 'primary',
}) => {
  const displayValue = isLoading ? '—' : (value ?? 0);

  const inner = (
    <div className="d-flex align-items-center justify-content-between gap-3">
      <div className="d-flex align-items-center gap-2 min-w-0">
        <span className={`badge rounded-pill text-bg-${accent}`}>{icon}</span>
        <div className="min-w-0">
          <div className="fw-semibold text-truncate">{displayValue}</div>
          <div className="small text-muted lh-sm">{label}</div>
        </div>
      </div>

      {isClickable && (
        <span className="text-muted" aria-hidden="true">
          →
        </span>
      )}
    </div>
  );

  const baseClass =
    'w-100 text-start rounded-4 border bg-white shadow-sm p-3 stat-pill';

  if (!isClickable) {
    return <div className={baseClass}>{inner}</div>;
  }

  return (
    <button
      type="button"
      className={`${baseClass} stat-pill--clickable`}
      onClick={onClick}
      disabled={isLoading}
    >
      {inner}
    </button>
  );
};

const Stars = ({ value }) => {
  const safeValue = Number(value) || 0;
  return <span className="fw-semibold">⭐ {safeValue.toFixed(1)}</span>;
};

const RatingButtons = ({ currentValue, onRate, isLoading }) => (
  <div className="btn-group btn-group-sm mt-2" role="group" aria-label="Rate player">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={`rating-${star}`}
        type="button"
        className={`btn ${currentValue === star ? 'btn-warning' : 'btn-outline-warning'}`}
        onClick={() => onRate?.(star)}
        disabled={isLoading}
      >
        {star}★
      </button>
    ))}
  </div>
);

const ProfileSocialStats = ({
  visitsCount,
  followersCount,
  followingCount,
  ratings,
  ratingActions,
  isLoading,
  onOpenFollowers,
  onOpenFollowing,
  disableInteractions = false,
  disabledReason,
}) => {
  const isOwner = Boolean(ratings?.isOwner);
  const receivedAvg = Number(ratings?.receivedAvg) || 0;
  const receivedCount = Number(ratings?.receivedCount) || 0;
  const myRating = ratings?.myRating ?? null;

  return (
    <div className="w-100">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div>
          <div className="small text-uppercase fw-semibold text-primary">
            Social stats
          </div>
          <div className="text-muted small">Visibility & network</div>
        </div>
        {!isLoading && (
          <span className="badge rounded-pill text-bg-light border">
            Live
          </span>
        )}
      </div>

      <div className="row g-2 row-cols-1 row-cols-md-3">
        <div className="col">
          <StatPill icon="👁️" label="Profile visits" value={visitsCount} isLoading={isLoading} accent="info" />
        </div>

        <div className="col">
          <StatPill
            icon="👥"
            label="Followers"
            value={followersCount}
            isLoading={isLoading}
            isClickable={!disableInteractions}
            onClick={disableInteractions ? undefined : onOpenFollowers}
            accent="primary"
          />
        </div>

        <div className="col">
          <StatPill
            icon="🧭"
            label="Following"
            value={followingCount}
            isLoading={isLoading}
            isClickable={!disableInteractions}
            onClick={disableInteractions ? undefined : onOpenFollowing}
            accent="success"
          />
        </div>
      </div>

      <div className="rounded-4 border bg-white shadow-sm p-3 mt-3">
        <div className="small text-uppercase fw-semibold text-primary">Ratings</div>
        {isLoading ? (
          <div className="text-muted mt-2">—</div>
        ) : isOwner ? (
          <>
            <div className="mt-1">
              <Stars value={receivedAvg} />
              <span className="text-muted"> {' '}({receivedCount} ratings)</span>
            </div>
            {ratings?.receivedBreakdown && (
              <div className="d-flex flex-wrap gap-2 mt-2 small">
                <span className="badge rounded-pill text-bg-light border">Player ⭐ {Number(ratings.receivedBreakdown.playerAvg || 0).toFixed(1)} ({ratings.receivedBreakdown.playerCount || 0})</span>
                <span className="badge rounded-pill text-bg-light border">Captain ⭐ {Number(ratings.receivedBreakdown.captainAvg || 0).toFixed(1)} ({ratings.receivedBreakdown.captainCount || 0})</span>
              </div>
            )}
            <div className="small text-muted mt-2">Ratings are private — visible only to you and the rater.</div>
          </>
        ) : (
          <>
            <div className="mt-1 d-flex align-items-center gap-2 flex-wrap">
              <span className="fw-semibold">Your rating:</span>
              {myRating ? <Stars value={myRating} /> : <span className="text-muted">Not rated yet</span>}
              {ratingActions?.onOpenEditor && !disableInteractions && (
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={ratingActions.onOpenEditor}>
                  {myRating ? 'Edit rating' : 'Rate player'}
                </button>
              )}
            </div>
            {ratingActions?.showEditor && !disableInteractions && (
              <RatingButtons currentValue={myRating} onRate={ratingActions.onRate} isLoading={isLoading} />
            )}
            <div className="small text-muted mt-2">Your rating is private (visible only to you and the player).</div>
          </>
        )}
        {disableInteractions && (
          <div className="small text-muted mt-2" title={disabledReason || 'This is a demo profile.'}>
            Interactions are disabled in the sample profile.
          </div>
        )}
      </div>

      <div className="text-muted small mt-2">
        Tip: A complete profile gets more visits and followers.
      </div>

      <style>{`
        .stat-pill {
          transition: transform .12s ease, box-shadow .12s ease, border-color .12s ease;
        }
        .stat-pill--clickable:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 .5rem 1.25rem rgba(15, 23, 42, .08);
          border-color: rgba(31, 162, 255, .35);
        }
        .stat-pill--clickable:active:not(:disabled) {
          transform: translateY(0px);
        }
        .stat-pill--clickable:focus-visible {
          outline: none;
          box-shadow: 0 0 0 .25rem rgba(31, 162, 255, .25);
          border-color: rgba(31, 162, 255, .55);
        }
      `}</style>
    </div>
  );
};

export default ProfileSocialStats;
